<template>
  <div class="checkout-form">
    <div class="form-header">
      <h5>{{ requires.shipping_section_title }}</h5>
    </div>
    <div class="form-body">
      <form id="shipping_form" @change="validateForm">
        <ValidationObserver ref="observer" slim>
          <div class="row">
            <div class="col-md-6">
              <ValidationProvider
                :rules="{ required: requires.shipping_location }"
                v-slot="{ errors, valid, validated, required }"
                :name="$t('checkout.firstname')"
                slim
              >
                <b-form-group
                  :invalid-feedback="errors[0]"
                  :state="required && validated ? valid : undefined"
                  :label="$t('checkout.firstname')"
                  label-class="label-required"
                  label-for="first-name"
                >
                  <b-form-input
                    id="first-name"
                    v-model="shipping_first_name"
                    :state="required && validated ? valid : undefined"
                    name="first_name"
                    type="text"
                    :placeholder="$t('checkout.firstname')"
                  />
                </b-form-group>
              </ValidationProvider>
            </div>
            <div class="col-md-6">
              <ValidationProvider
                :rules="{ required: requires.shipping_location }"
                v-slot="{ errors, valid, validated, required }"
                :name="$t('checkout.lastname')"
                slim
              >
                <b-form-group
                  :invalid-feedback="errors[0]"
                  :state="required && validated ? valid : undefined"
                  :label="$t('checkout.lastname')"
                  label-class="label-required"
                  label-for="last-name"
                >
                  <b-form-input
                    id="first-name"
                    v-model="shipping_last_name"
                    :state="required && validated ? valid : undefined"
                    name="last_name"
                    type="text"
                    :placeholder="$t('checkout.lastname')"
                  />
                </b-form-group>
              </ValidationProvider>
            </div>
          </div>

          <ValidationProvider
            v-slot="{ errors, valid, validated, required }"
            v-if="requires.shipping_show_business_name"
            :name="$t('address.businessname')"
            slim
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :state="required && validated ? valid : undefined"
              :label="$t('address.businessname')"
              label-class="label-required"
              label-for="business_name"
            >
              <b-form-input
                id="business_name"
                v-model="business_name"
                :state="required && validated ? valid : undefined"
                name="business_name"
                type="text"
                :placeholder="$t('address.businessname')"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            v-slot="{ errors, valid, validated, required }"
            v-if="requires.shipping_show_vatin"
            name="VAT ID"
            slim
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :state="required && validated ? valid : undefined"
              label="VAT ID"
              label-class=""
              label-for="shipping_vatin"
            >
              <b-form-input
                id="shipping_vatin"
                v-model="shipping_vatin"
                :state="required && validated ? valid : undefined"
                name="shipping_vatin"
                type="text"
                placeholder="VAT ID"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            v-slot="{ errors, valid, validated, required }"
            v-if="requires.shipping_show_phone"
            :name="$t('address.phone')"
            slim
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :state="required && validated ? valid : undefined"
              :label="$t('address.phone')"
              label-class="label-required"
              label-for="shipping_phone"
            >
              <b-form-input
                id="shipping_phone"
                v-model="shipping_phone"
                :state="required && validated ? valid : undefined"
                name="shipping_phone"
                type="text"
                :placeholder="$t('address.phone')"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            :rules="{ required: requires.shipping_location }"
            v-slot="{ errors, valid, validated, required }"
            :name="$t('address.street1')"
            slim
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :state="required && validated ? valid : undefined"
              :label="$t('address.street1')"
              label-class="label-required"
              label-for="address1"
            >
              <b-form-input
                id="address1"
                v-model="shipping_address1"
                :state="required && validated ? valid : undefined"
                name="address1"
                type="text"
                :placeholder="$t('address.street1')"
              />
            </b-form-group>
          </ValidationProvider>

          <div class="form-group">
            <label for="address2">{{ $t("address.street2") }}</label>
            <input
              v-model="shipping_address2"
              class="input form-control"
              name="address2"
              type="text"
              :placeholder="$t('address.street2')"
            />
          </div>

          <ValidationProvider
            :rules="{ required: requires.shipping_location }"
            v-slot="{ errors, valid, validated, required }"
            :name="$t('address.country')"
            slim
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :state="required && validated ? valid : undefined"
              :label="$t('address.country')"
              label-class="label-required"
              label-for="country"
            >
              <b-form-select
                v-model.lazy="country_code"
                :options="shipping.countries"
                :state="required && validated ? valid : undefined"
                option-value="value"
                option-text="value"
                id="country"
              />
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            :rules="{ required: requires.shipping_location }"
            v-slot="{ errors, valid, validated, required }"
            :name="$t('address.city')"
            slim
          >
            <b-form-group
              :invalid-feedback="errors[0]"
              :state="required && validated ? valid : undefined"
              :label="$t('address.city')"
              label-class="label-required"
              label-for="city"
            >
              <b-form-input
                id="city"
                v-model.lazy="shipping_city"
                :state="required && validated ? valid : undefined"
                name="city"
                type="text"
                :placeholder="$t('address.city')"
              />
            </b-form-group>
          </ValidationProvider>

          <div class="row">
            <div class="col-md-8">
              <ValidationProvider
                :rules="{
                  required:
                    requires.shipping_location && shipping.zones.length > 1,
                }"
                v-slot="{ errors, valid, validated, required }"
                :name="$t('address.state')"
                slim
              >
                <b-form-group
                  :invalid-feedback="errors[0]"
                  :state="required && validated ? valid : undefined"
                  :label="$t('address.state')"
                  label-class="label-required"
                  label-for="state"
                >
                  <b-form-select
                    v-if="shipping.zones.length > 1"
                    v-model.lazy="province"
                    :options="shipping.zones"
                    :state="required && validated ? valid : undefined"
                    option-value="value"
                    option-text="value"
                    id="province"
                  />

                  <b-form-input
                    v-else
                    id="province"
                    v-model.lazy="province"
                    :state="required && validated ? valid : undefined"
                    type="text"
                    :placeholder="$t('address.state')"
                  />
                </b-form-group>
              </ValidationProvider>
            </div>
            <div class="col-md-4">
              <ValidationProvider
                :rules="{ required: requires.shipping_location }"
                v-slot="{ errors, valid, validated, required }"
                :name="$t('address.zip')"
                slim
              >
                <b-form-group
                  :invalid-feedback="errors[0]"
                  :state="required && validated ? valid : undefined"
                  :label="$t('address.zip')"
                  label-class="label-required"
                  label-for="postal_code"
                >
                  <b-form-input
                    id="postal_code"
                    v-model.lazy="shipping_postal_code"
                    :state="required && validated ? valid : undefined"
                    name="postal_code"
                    type="text"
                    :placeholder="$t('address.zip')"
                  />
                </b-form-group>
              </ValidationProvider>
            </div>
          </div>
          <div v-if="requires.shipping" class="row">
            <div class="form-group col-md-12">
              <label for="city">
                {{ $t("checkout.shippingmethod") }}
                <span>*</span>
              </label>
              <b-form-select
                :options="shipping.options"
                v-model="shipping_option_id"
                option-value="value"
                option-text="value"
                @change.native.stop
              />
            </div>
          </div>
        </ValidationObserver>
      </form>
    </div>
  </div>
</template>

<script>
/* ============
 * Checkout/Shipping Component
 * ============
 *
 * Gives an idea how components work.
 */
import { mapState } from "vuex";
import { sync, get } from "vuex-pathify";

//import { _ } from "lodash";

export default {
  /**
   * The name of the component.
   */
  name: "CheckoutShipping",

  /**
   * The computed properties that the component can use.
   */
  computed: {
    ...mapState({
      cart: (state) => state.cart,
      requires: (state) => state.requires,
      shipping: (state) => state.shipping,
    }),
    country_code: {
      get: get("shipping@location.country_code"),
      set(value) {
        this.$store.commit("setCountryCode", value);
        this.$store.dispatch("setZones", value);
      },
    },
    province: sync("shipping@location.province"),
    shipping_address1: sync("shipping@location.address1"),
    shipping_address2: sync("shipping@location.address2"),
    shipping_city: sync("shipping@location.city"),
    shipping_postal_code: sync("shipping@location.postal_code"),
    shipping_first_name: sync("shipping@location.first_name"),
    shipping_last_name: sync("shipping@location.last_name"),
    shipping_phone: sync("shipping@location.phone"),
    shipping_vatin: sync("shipping@location.vatin"),
    business_name: sync("shipping@location.business_name"),
    shipping_option_id: {
      get: get("shipping@option_id"),
      set(value) {
        this.$store.set("shipping@option_id", value);
        this.$store.dispatch("postShippingOption", false);
      },
    },
  },

  methods: {
    getNewShippingOptions() {
      this.$store.dispatch("getNewShippingOptions");
    },
    async validateForm() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        let loader = this.$loading.show();
        await this.$store.dispatch("postShipping");
        loader.hide();
      }
    },
  },
};
</script>
